import { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";

/**
 * Web "Update available — tap to restart" banner. Listens for the
 * `sw-update-available` event dispatched from index.js when a new service worker
 * is waiting, then on click tells it to skipWaiting → the page reloads into the
 * new build (handled by the controllerchange listener in index.js).
 */
export default function UpdateBanner() {
  const [visible, setVisible] = useState(false);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    const onAvail = () => setVisible(true);
    window.addEventListener("sw-update-available", onAvail);
    if (window.__swWaiting) setVisible(true);
    return () => window.removeEventListener("sw-update-available", onAvail);
  }, []);

  const restart = () => {
    setApplying(true);
    const w = window.__swWaiting;
    if (w) {
      w.postMessage({ type: "SKIP_WAITING" });
      // Safety net: if controllerchange doesn't fire, reload anyway.
      setTimeout(() => window.location.reload(), 1500);
    } else {
      window.location.reload();
    }
  };

  if (!visible) return null;

  return (
    <button
      type="button"
      onClick={restart}
      disabled={applying}
      data-testid="web-update-restart-btn"
      className="fixed bottom-4 left-1/2 z-[2000] -translate-x-1/2 flex items-center gap-3 rounded-2xl bg-[#111114] px-4 py-3 text-left text-white shadow-2xl transition-opacity hover:opacity-95 disabled:opacity-80"
    >
      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/15">
        <RefreshCw size={16} className={applying ? "animate-spin" : ""} />
      </span>
      <span className="leading-tight">
        <span className="block text-sm font-bold">{applying ? "Updating…" : "Update available"}</span>
        <span className="block text-xs text-white/60">{applying ? "Reloading" : "Tap to restart and apply"}</span>
      </span>
      {!applying && <span className="ml-1 rounded-lg px-2 py-1 text-xs font-bold">Restart</span>}
    </button>
  );
}
